import React, { forwardRef } from 'react';
import { Speech } from 'lucide-react';

interface WordDefinitionBubbleProps {
    word: string | null;
    wordInfo: {
        definition: string;
        partOfSpeech: string;
        explanation: string;
        audio: string;
    } | null;
    position: { top: number; left: number };
    onClose: () => void;
    onPronounce: (audioPath: string) => void;
    isPlaying: boolean;
    audioFinished: boolean;
}

const WordDefinitionBubble = forwardRef<HTMLDivElement, WordDefinitionBubbleProps>(({
    word,
    wordInfo,
    position,
    onClose,
    onPronounce,
    isPlaying,
    audioFinished
}, ref) => {
    if (!word || !wordInfo) return null;
    const isButtonDisabled = isPlaying && !audioFinished;

    return (
        <div
            ref={ref}
            className="fixed bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 transition-opacity duration-300 ease-in-out"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                opacity: 1,
                width: '400px', // Increased from 320px (w-80) to 400px
                maxWidth: '90vw'
            }}
        >
            <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                onClick={onClose}
            >
                ×
            </button>
            <h3 className="font-bold mb-2">{word}</h3>
            <div className="flex justify-between mb-2 text-sm">
                <p className="mr-2">
                    <strong>Definition:</strong> {wordInfo.definition || "Not available."}
                </p>
                <p className="ml-2">
                    <strong>Part of Speech:</strong> {wordInfo.partOfSpeech || "Not available."}
                </p>
            </div>
            <p className="mb-4 text-sm">
                <strong>Explanation:</strong> {wordInfo.explanation || "Not available."}
            </p>
            <button
                className={`flex items-center justify-center w-full py-2 px-4 rounded text-base ${
                    isButtonDisabled
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        : 'bg-greek-black text-white hover:bg-greek-black/90'
                }`}
                onClick={() => onPronounce(wordInfo.audio || '')}
                disabled={isButtonDisabled}
            >
                <Speech className="mr-2" size={20} />
                {isPlaying ? 'Playing...' : 'Pronounce'}
            </button>
        </div>
    );
});

export default WordDefinitionBubble;